import { Box } from '@chakra-ui/react';
import { getCountryFieldData } from 'utils/countries';

import { FormControl, FormLabel, Input, FormSelect, FormSelectProps } from 'components/UI';
import { FormikHandlers, FormikHelpers } from 'formik';

type CountryStateValues = {
  country: string;
  state: string;
  zipCode: string;
};

type CountryStateFieldsProps = {
  countries: { name: string; iso2: string }[];
  countryStatesLoading: boolean;
  values: CountryStateValues;
  errors: Record<keyof CountryStateValues, string>;
  possessiveAdj?: string;
  handleBlur: FormikHandlers['handleBlur'];
  handleChange: FormikHandlers['handleChange'];
  setFieldValue: FormikHelpers<CountryStateValues | Record<string, never>>['setFieldValue'];
  setFieldTouched: FormikHelpers<CountryStateValues | Record<string, never>>['setFieldTouched'];
  countryStates: { name: string; iso2: string }[];
  onChangeCountry: (country: string) => void;
};

const CountryStateFields = ({
  countryStatesLoading,
  countries,
  values,
  errors,
  possessiveAdj = 'the',
  setFieldValue,
  setFieldTouched,
  countryStates,
  handleChange,
  handleBlur,
  onChangeCountry,
}: CountryStateFieldsProps) => {
  const selectProps: Omit<FormSelectProps, 'name'> = {
    filterConfig: {
      matchFrom: 'start',
    },
    setFieldValue,
    setFieldTouched,
  };

  const {
    postalZipFieldText,
    postalZipFieldTextAsterisk,
    postalZipFieldLength,
    provinceStateFieldText,
    provinceStateFieldPlaceholder,
    isCountry,
    isOther,
  } = getCountryFieldData(values.country);

  const renderStateField = isCountry ? (
    <Box>
      <FormControl isInvalid={!!errors.state}>
        <FormLabel htmlFor="state">{provinceStateFieldText.toUpperCase()} *</FormLabel>
        <FormSelect
          name="state"
          error={errors.state}
          value={values.state}
          placeholder={`Select ${possessiveAdj} ${provinceStateFieldPlaceholder}`}
          options={countryStates.map(({ name, iso2 }) => ({ label: name, value: iso2 }))}
          isDisabled={countryStatesLoading}
          {...selectProps}
        />
      </FormControl>
    </Box>
  ) : null;

  const renderZipCodeField = isCountry ? (
    <Box>
      <FormControl isInvalid={!!errors.zipCode}>
        <FormLabel htmlFor="zipCode">
          {postalZipFieldText.toUpperCase()} CODE {postalZipFieldTextAsterisk}
        </FormLabel>

        <Input
          name="zipCode"
          error={errors.zipCode}
          maxLength={postalZipFieldLength}
          value={values.zipCode}
          onChange={(e) => {
            setFieldValue('zipCode', e.target.value.toUpperCase());
          }}
          onBlur={handleBlur}
          placeholder={`Enter ${possessiveAdj} ${postalZipFieldText} code`}
        />
      </FormControl>
    </Box>
  ) : null;

  const renderCountryOtherField = isOther ? (
    <Box gridColumn={isOther ? 'span 2' : 'span 1'}>
      <FormControl isInvalid={!!errors.state}>
        <FormLabel htmlFor="state">COUNTRY *</FormLabel>
        <Input
          name="state"
          autoComplete="off"
          maxLength={20}
          value={values.state}
          error={errors.state}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={`Enter ${possessiveAdj} country`}
        />
      </FormControl>
    </Box>
  ) : null;

  return (
    <>
      <Box>
        <FormControl isInvalid={!!errors.country}>
          <FormLabel htmlFor="country">COUNTRY *</FormLabel>
          <FormSelect
            name="country"
            error={errors.country}
            value={values.country}
            placeholder={`Select ${possessiveAdj} country`}
            options={countries.map(({ name, iso2 }) => ({ label: name, value: iso2 }))}
            onChange={(value) => {
              setFieldValue('state', null);
              setFieldValue('zipCode', '');
              setFieldTouched('state', false);
              setFieldTouched('zipCode', false);
              if (!onChangeCountry) return;
              onChangeCountry(value as string);
            }}
            {...selectProps}
          />
        </FormControl>
      </Box>
      {renderStateField}
      {renderZipCodeField}
      {renderCountryOtherField}
    </>
  );
};

export default CountryStateFields;
